import { createContext, useCallback, useContext, useRef } from 'react';

/**
 * Historique de navigation entre les écrans, comme dans la maquette V288 :
 * chaque changement d'écran empile celui qu'on quitte, et la flèche « ← » de
 * l'en-tête y revient. Sans lui, seules les pages qui le prévoyaient (une
 * fiche ouverte) avaient une flèche ; les Archives, la Compta ou les Réglages
 * n'offraient aucun retour vers l'écran d'où l'on venait.
 */

/** Profondeur de la pile : au-delà, les écrans les plus anciens sont oubliés. */
export const PROFONDEUR_HISTORIQUE = 40;

/**
 * La pile après un passage de `courant` à `cible`.
 *
 * Rouvrir l'écran déjà affiché n'empile rien : la flèche ramènerait sinon sur
 * le même écran.
 *
 * @param {string[]} pile écrans précédents, le plus récent en dernier
 * @param {string} courant écran quitté
 * @param {string} cible écran ouvert
 * @returns {string[]} la nouvelle pile (l'ancienne n'est pas modifiée)
 */
export function empiler(pile, courant, cible) {
  if (!cible || cible === courant || !courant) return pile;
  const suite = [...pile, courant];
  return suite.length > PROFONDEUR_HISTORIQUE ? suite.slice(-PROFONDEUR_HISTORIQUE) : suite;
}

/**
 * L'écran où ramène la flèche, et la pile qui reste.
 *
 * Une pile vide ramène à l'accueil, comme la maquette.
 *
 * @param {string[]} pile écrans précédents
 * @returns {{ ecran: string, pile: string[] }} écran à rouvrir et pile restante
 */
export function depiler(pile) {
  if (!pile.length) return { ecran: 'accueil', pile };
  return { ecran: pile[pile.length - 1], pile: pile.slice(0, -1) };
}

/**
 * Ce que l'en-tête lit de l'historique : `null` hors de l'application (écrans
 * de connexion, tests d'une page seule), où aucune flèche n'est proposée.
 *
 * @type {import('react').Context<{peutRevenir: boolean, revenir: () => void}|null>}
 */
export const HistoriqueNavigation = createContext(null);

/** @returns {{peutRevenir: boolean, revenir: () => void}|null} l'historique courant */
export const useHistoriqueNavigation = () => useContext(HistoriqueNavigation);

/**
 * Tient la pile de l'application autour de l'état `nav`.
 *
 * La pile vit dans une référence : la changer n'a pas à redessiner, puisque
 * chaque mouvement change aussi `nav`, qui redessine.
 *
 * @param {string} nav écran courant
 * @param {(ecran: string) => void} setNav change l'écran sans toucher à la pile
 * @returns {{aller: (cible: string) => void, revenir: () => void, oublier: () => void, peutRevenir: boolean}}
 */
export function usePileNavigation(nav, setNav) {
  const pile = useRef([]);
  const aller = useCallback(
    (cible) => {
      pile.current = empiler(pile.current, nav, cible);
      setNav(cible);
    },
    [nav, setNav],
  );
  const revenir = useCallback(() => {
    const { ecran, pile: reste } = depiler(pile.current);
    pile.current = reste;
    setNav(ecran);
  }, [setNav]);
  const oublier = useCallback(() => {
    pile.current = [];
  }, []);
  return { aller, revenir, oublier, peutRevenir: pile.current.length > 0 };
}
